/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme FIQ has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Custom code for Appstle subscription compare amount modification
(function() {
  // Function to process appstle subscription elements
  function processAppstleElements() {
    const subscriptionWrappers = document.querySelectorAll('.product-item .appstle_subscription_amount_wrapper:not([data-processed])');
    
    subscriptionWrappers.forEach(function(wrapper) {
      // Mark as processed to avoid duplicate processing
      wrapper.setAttribute('data-processed', 'true');
      
      // Add the custom select span to the wrapper
      const customSelectSpan = document.createElement('span');
      customSelectSpan.setAttribute('class', 'appstle_custom_select');
      wrapper.appendChild(customSelectSpan);
      
      // Add click event listener to the wrapper to toggle expand class on nearest parent .product-item__cta-wrapper
      wrapper.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Find the nearest parent with class .product-item__cta-wrapper
        const ctaWrapper = wrapper.closest('.product-item').querySelector('.product-item__cta-wrapper');
        if (ctaWrapper) {
          ctaWrapper.classList.toggle('expand');
        }
      });
    });
  }

  // Process elements on initial load
  document.addEventListener('DOMContentLoaded', processAppstleElements);
  
  // Use MutationObserver to watch for dynamically added content
  const observer = new MutationObserver(function(mutations) {
    let shouldProcess = false;
    
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node contains appstle elements or is an appstle element itself
            if (node.classList && (node.classList.contains('appstle_subscription_amount_wrapper') || 
                node.classList.contains('appstle_subscription_compare_amount')) ||
                node.querySelector && node.querySelector('.appstle_subscription_amount_wrapper')) {
              shouldProcess = true;
            }
          }
        });
      }
    });
    
    if (shouldProcess) {
      processAppstleElements();
    }
  });

  // Start observing the document with the configured parameters
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // Also run the function periodically as a fallback for complex dynamic loading
  setInterval(processAppstleElements, 1000);
})();


// Mini-cart checkout button guard:
// While the B2B app recalculates the cart total after a quantity change, it briefly
// empties the total on the checkout button and the button can end up disabled.
// Keep the button clickable and show a white spinner in place of the total.
(function () {
  function ensureSpinner(totalSpan) {
    if (totalSpan.querySelector('.checkout-total-spinner')) return;
    var spinner = document.createElement('span');
    spinner.className = 'checkout-total-spinner';
    spinner.setAttribute('role', 'status');
    spinner.setAttribute('aria-label', 'Updating total');
    totalSpan.appendChild(spinner);
  }

  // While a quantity change is in flight we show the spinner. Once the drawer
  // re-renders, the theme prints the fresh (already correct) total; if the B2B
  // app then blanks it while it re-checks, we restore that total right away
  // instead of leaving a 2-3 second gap.
  var updating = false;
  var lastTotal = '';

  function guardCheckoutButton() {
    var button = document.getElementById('mini-cart-checkout');
    if (!button) return;

    // Keep the button clickable while the total updates
    if (button.hasAttribute('disabled')) button.removeAttribute('disabled');
    if (button.getAttribute('aria-disabled') === 'true') button.removeAttribute('aria-disabled');
    if (getComputedStyle(button).pointerEvents === 'none') button.style.pointerEvents = 'auto';

    var totalSpan = button.querySelector('[bss-b2b-cart-total-price]');
    if (!totalSpan) return;

    var text = totalSpan.textContent.trim();
    if (text !== '') {
      // A real total is visible: remember it and end the updating window.
      if (!totalSpan.querySelector('.checkout-total-spinner')) {
        lastTotal = text;
        updating = false;
      }
      return;
    }

    // Total is blank. During the update window show the spinner;
    // afterwards restore the last known total immediately.
    if (!updating && lastTotal !== '') {
      totalSpan.textContent = lastTotal;
    } else {
      ensureSpinner(totalSpan);
    }
  }

  // Show the spinner immediately when a line-item quantity changes,
  // so there is never a blank total even before the B2B app kicks in.
  document.addEventListener('line-item-quantity:change:start', function () {
    var button = document.getElementById('mini-cart-checkout');
    if (!button) return;
    var totalSpan = button.querySelector('[bss-b2b-cart-total-price]');
    if (!totalSpan) return;
    updating = true;
    lastTotal = '';
    totalSpan.textContent = '';
    ensureSpinner(totalSpan);
  }, true);

  var scheduled = false;
  var observer = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      guardCheckoutButton();
    });
  });

  function start() {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'style', 'class']
    });
    guardCheckoutButton();
    setInterval(guardCheckoutButton, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

// Wholesale price spacing normalizer:
// The B2B app injects a crossed-out original price with markup we cannot predict.
// Find any struck-through element inside a BSS-tagged price span (computed style,
// so it works regardless of the markup) and give it consistent spacing.
(function () {
  if (!window.fiqWholesale) return;

  var SELECTOR = '[bss-b2b-product-price], [bss-b2b-variant-price], [bss-b2b-product-lowest-price]';

  function normalize() {
    document.querySelectorAll(SELECTOR).forEach(function (priceEl) {
      // Only products WITH a compare-at price get a gap; the theme renders a
      // (hidden) .price--compare sibling for those. Others get no extra gap.
      var priceList = priceEl.closest('.price-list');
      var hasCompareAt = priceList ? !!priceList.querySelector('.price--compare') : true;
      var gap = hasCompareAt ? '0.35em' : '0';

      priceEl.querySelectorAll('*').forEach(function (child) {
        if (child.dataset.fiqSpaced === gap) return;
        var style = getComputedStyle(child);
        if (style.textDecorationLine.indexOf('line-through') !== -1) {
          child.style.marginRight = gap;
          child.dataset.fiqSpaced = gap;
        }
      });
    });
  }

  var scheduled = false;
  var observer = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      normalize();
    });
  });

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    normalize();
    setInterval(normalize, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

if (window.location.pathname === "/apps/wholesale-pro/wholesale-section") {
  window.location.replace("/");
}